import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useSlikHarAndreTallfestet, type MetricExample } from '../api/useHypoteseHukommelse.ts';
import { useKilder, useLukkSporsmaal, useSpoerEieren, type MetricSource, type MetricSourceRef, type SourceQuestion } from '../api/useHypoteseKilder.ts';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { tokenVar } from '../status/status.ts';
import { Brikker, Tekstknapp, Varsel } from './deler.tsx';
import { brukesAv, kildeMeta, kildestatus } from './kilder.ts';
import { nummerTekst } from './Kvittering.tsx';
import { fornavn, navnAv } from './lesning.ts';
import { kortDato, kortDatoAv, retningOrd, tall, type Del } from './setning.ts';

/**
 * «Jeg vet ikke tallet ennå» (H3.3, konseptsiden W5–W7, M2–M5; I-Jw): tre veier i fast rekkefølge —
 * «Slik har andre tallfestet», kildekatalogen og «Spør eieren av kilden».
 *
 * Over 900 px er det et SIDEPANEL ved siden av skjemaet (DD-47 presisert 2026-10-29): `<aside>` med
 * overskrift, ingen fokusfelle og ingen scrim — feltet til venstre er skrivbart hele tiden; Esc i
 * panelet lukker det og setter fokus tilbake på utløseren; det panelet fyller inn, sies i én
 * statuslinje øverst, og panelet blir stående; høyst én fylt knapp, og den lagrer aldri skjemaet.
 * Under 900 px er det samme innholdet et modalt ark (en dialog etter DD-47).
 */

export type Vei = 'veier' | 'kilder' | 'spor';

interface Props {
  id: string | null;
  start: Vei;
  smal: boolean;
  tenant: string;
  megId: string | undefined;
  /** Setningen som brikker, for den sammenklappede linjen øverst. */
  deler: Del[];
  /** Kilden utkastet bruker fra katalogen — den spørsmålet går til. */
  kilde: MetricSourceRef | undefined;
  /** Spørsmålet utkastet har ute (åpent, eller besvart og ventende). */
  sporsmaal: SourceQuestion | undefined;
  melding: string | null;
  panelId: string;
  utloeser: RefObject<HTMLButtonElement | null>;
  onBrukKilde: (k: MetricSource) => void;
  onSkrivSelv: () => void;
  onLukk: () => void;
}

export function VetIkkeTallet(p: Props) {
  const [vei, setVei] = useState<Vei>(p.start);
  const [visHele, setVisHele] = useState(false);
  const kilder = useKilder();
  const tittel = vei === 'kilder' ? `Kilder hos ${p.tenant}` : vei === 'spor' ? (p.kilde ? `Spør ${navnAv(p.kilde.owner)} om ${p.kilde.name.toLocaleLowerCase('nb')}` : 'Spør eieren av kilden') : 'Jeg vet ikke tallet ennå';

  const lukk = () => {
    p.onLukk();
    setTimeout(() => p.utloeser.current?.focus(), 0);
  };
  const paaTast = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    lukk();
  };

  // W5: setningen klappet til én linje — bindeordet foran og brikken som skrives nå — med «Vis hele».
  const aapent = p.deler.findIndex((d) => d.type === 'on');
  const utsnitt: Del[] =
    aapent < 1
      ? p.deler
      : [{ type: 'fw', tekst: `… ${p.deler[aapent - 1]!.tekst.replace(/^[,.]\s*/, '')}` }, p.deler[aapent]!, ...(p.deler[aapent + 1] ? [{ type: 'fw' as const, tekst: '…' }] : [])];
  const setning = p.smal ? null : (
    <div className="h-panel-setning">
      <Brikker deler={visHele ? p.deler : utsnitt} />
      <Tekstknapp onClick={() => setVisHele((v) => !v)}>{visHele ? 'Vis mindre' : 'Vis hele'}</Tekstknapp>
    </div>
  );

  const innhold = (
    <>
      {setning}
      {p.melding ? (
        <p role="status" className="h-panel-melding">
          <Ikon navn="checkmark-outline" />
          {p.melding}
        </p>
      ) : (
        <span role="status" className="panel-skjult" />
      )}
      {vei !== 'veier' ? (
        <span className="h-panel-tilbake">
          <Tekstknapp onClick={() => setVei('veier')}>
            <Ikon navn="chevron-back-outline" />
            Tilbake
          </Tekstknapp>
        </span>
      ) : null}
      <div className="h-panel-hode">
        <h2 id="h-panel-h" className="h-panel-tittel">
          {tittel}
        </h2>
        <button type="button" className="panel-knapp-lenke h-noeytral h-panel-lukk" aria-label="Lukk" aria-keyshortcuts="Escape" onClick={lukk}>
          {p.smal ? <Ikon navn="close-outline" /> : <>Lukk {!p.smal ? <kbd>Esc</kbd> : null}</>}
        </button>
      </div>
      {vei === 'veier' ? <Veier id={p.id} smal={p.smal} tenant={p.tenant} antall={kilder.data?.items.length} onKilder={() => setVei('kilder')} onSpor={() => setVei('spor')} /> : null}
      {vei === 'kilder' ? <Kildeliste tenant={p.tenant} valgt={p.kilde?.id} onBruk={p.onBrukKilde} onSkrivSelv={p.onSkrivSelv} /> : null}
      {vei === 'spor' ? <Spor id={p.id} tenant={p.tenant} megId={p.megId} kilde={p.kilde} sporsmaal={p.sporsmaal} onKilder={() => setVei('kilder')} /> : null}
    </>
  );

  if (p.smal) {
    return (
      <Dialog tittelId="h-panel-h" onLukk={p.onLukk} className="h-ark" testid="h-panel">
        {innhold}
      </Dialog>
    );
  }
  return (
    <aside id={p.panelId} aria-labelledby="h-panel-h" className="h-panel" onKeyDown={paaTast} data-testid="h-panel">
      {innhold}
    </aside>
  );
}

/** Tallene i rekkefølge, tallene halvfete: «18 % → rett hvis minst 22 % · ble 27 %» (W5). */
function EksempelTall({ e }: { e: MetricExample }) {
  const r = e.result;
  const enh = (v: number) => `${tall(v)} ${r.unit}`.trim();
  return (
    <span className="hb-dempet h-panel-eksempel-tall">
      {r.baselineValue != null ? (
        <>
          <b>{enh(r.baselineValue)}</b> →{' '}
        </>
      ) : null}
      rett hvis {retningOrd(r.direction)} <b>{enh(r.threshold)}</b> · ble <b>{enh(r.resultValue)}</b>
    </span>
  );
}

function Veier({ id, smal, tenant, antall, onKilder, onSpor }: { id: string | null; smal: boolean; tenant: string; antall: number | undefined; onKilder: () => void; onSpor: () => void }) {
  // PC: den første veien står åpen (W5). Mobil: tre veier, hver åpner inne i arket (M2).
  const [aapen, setAapen] = useState(!smal);
  const eksempler = useSlikHarAndreTallfestet(id, aapen);
  return (
    <div className="h-panel-veier">
      <button type="button" className="h-panel-vei" aria-expanded={aapen} aria-controls="h-panel-eksempler" onClick={() => setAapen((v) => !v)}>
        <Ikon navn="book-outline" />
        <span className="h-panel-vei-navn">Slik har andre tallfestet</span>
        <Ikon navn="chevron-forward-outline" />
      </button>
      {aapen ? (
        <div id="h-panel-eksempler" className="h-panel-eksempler">
          {eksempler.isPending ? (
            <div role="status" aria-busy="true" aria-label="Henter eksemplene" className="panel-skjelett">
              <span className="panel-skjelett-linje" />
              <span className="panel-skjelett-linje panel-skjelett-kort" />
            </div>
          ) : eksempler.isError ? (
            <p className="hb-dempet">Eksemplene kunne ikke hentes.</p>
          ) : eksempler.data.length === 0 ? (
            <p className="hb-dempet">Ingen hos {tenant} har konkludert en lignende ennå.</p>
          ) : (
            <ul aria-label="Slik har andre tallfestet" className="h-panel-eksempelliste">
              {eksempler.data.map((e) => (
                <li key={e.hypothesis.id} className="h-panel-eksempel">
                  <span className="h-panel-eksempel-tittel">
                    {e.hypothesis.title} · {nummerTekst(e.hypothesis.number)}
                  </span>
                  <span>
                    {e.metricName} · {e.sourceLabel}
                  </span>
                  <EksempelTall e={e} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      <button type="button" className="h-panel-vei" onClick={onKilder}>
        <Ikon navn="server-outline" />
        <span className="h-panel-vei-navn">Kilder hos {tenant}</span>
        {antall !== undefined ? <span className="h-panel-vei-tall">{antall}</span> : null}
        <Ikon navn="chevron-forward-outline" />
      </button>
      <button type="button" className="h-panel-vei" onClick={onSpor}>
        <Ikon navn="person-outline" />
        <span className="h-panel-vei-navn">Spør eieren av kilden</span>
        <Ikon navn="chevron-forward-outline" />
      </button>
    </div>
  );
}

/** Kildekortene (W6, M3–M5): skjelett mens de lastes, tom som invitasjon, feil som feil — aldri «ingen». */
function Kildeliste({ tenant, valgt, onBruk, onSkrivSelv }: { tenant: string; valgt: string | undefined; onBruk: (k: MetricSource) => void; onSkrivSelv: () => void }) {
  const [sok, setSok] = useState('');
  const kilder = useKilder(sok.trim() ? { q: sok.trim() } : {});
  const sokRef = useRef<HTMLInputElement>(null);
  useEffect(() => sokRef.current?.focus(), []);
  let liste: ReactNode;
  if (kilder.isPending) {
    liste = (
      <div role="status" aria-busy="true" aria-label="Henter kildene" className="h-panel-kort-liste">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-kildekort h-kildekort-skjelett">
            <span className="panel-skjelett-linje panel-skjelett-tittel" />
            <span className="panel-skjelett-linje" />
            <span className="panel-skjelett-linje panel-skjelett-kort" />
          </div>
        ))}
      </div>
    );
  } else if (kilder.isError) {
    liste = (
      <Varsel
        tone="info"
        handling={
          <span className="h-panel-varsel-valg">
            <Tekstknapp onClick={() => void kilder.refetch()}>Prøv igjen</Tekstknapp>
            <Tekstknapp onClick={onSkrivSelv}>Skriv kilden selv</Tekstknapp>
          </span>
        }
      >
        Kildene kunne ikke hentes. Du kan skrive kilden selv.
      </Varsel>
    );
  } else if (kilder.data.items.length === 0 && !sok.trim()) {
    liste = (
      <div className="h-panel-tom">
        <p className="h-panel-tom-tittel">{tenant} har ikke lagt inn kilder ennå.</p>
        <p className="hb-dempet">Skriv kilden selv i feltet — vurdereren eller den som eier kilden bekrefter den.</p>
        <div>
          <button type="button" className="panel-knapp" onClick={onSkrivSelv}>
            <Ikon navn="create-outline" />
            Skriv kilden selv
          </button>
        </div>
      </div>
    );
  } else if (kilder.data.items.length === 0) {
    liste = <p className="hb-dempet">Ingen kilder treffer «{sok.trim()}». Finner du ikke kilden, skriv den selv i feltet.</p>;
  } else {
    liste = (
      <div className="h-panel-kort-liste">
        {kilder.data.items.map((k) => {
          const st = kildestatus(k.status);
          return (
            <article key={k.id} className="h-kildekort" aria-labelledby={`h-kk-${k.id}`}>
              <h3 id={`h-kk-${k.id}`} className="h-kildekort-navn">
                {k.name}
              </h3>
              <p className="h-kildekort-maaler">{k.measures}</p>
              <p className="h-kildekort-meta">
                <span className="h-kildestatus" style={{ color: tokenVar(st.fg) }}>
                  <Ikon navn={st.ikon} />
                </span>
                {kildeMeta(k)}
              </p>
              <div className="h-kildekort-fot">
                <span className="hb-dempet">{brukesAv(k.hypothesisCount)}</span>
                {valgt === k.id ? (
                  <span className="h-kildekort-valgt">
                    <Ikon navn="checkmark-outline" />
                    Brukes i utkastet
                  </span>
                ) : (
                  <button type="button" className="panel-knapp-sekundaer" onClick={() => onBruk(k)}>
                    Bruk denne kilden
                  </button>
                )}
              </div>
            </article>
          );
        })}
        <p className="hb-dempet">Finner du ikke kilden, skriv den selv i feltet.</p>
      </div>
    );
  }
  return (
    <div className="h-panel-kilder">
      <label className="panel-sok h-panel-sok">
        <Ikon navn="search-outline" />
        <input ref={sokRef} type="search" value={sok} onChange={(e) => setSok(e.target.value)} aria-label="Søk i kildene" placeholder="Søk i kildene" />
      </label>
      {liste}
    </div>
  );
}

const MIN_SPORSMAAL = 10;

/** «Spør eieren av kilden» (W7, I-S): ett felt, én fast setning om hva eieren ser (Kh15), og panelets ene fylte knapp. */
function Spor({ id, tenant, megId, kilde, sporsmaal, onKilder }: { id: string | null; tenant: string; megId: string | undefined; kilde: MetricSourceRef | undefined; sporsmaal: SourceQuestion | undefined; onKilder: () => void }) {
  const [tekst, setTekst] = useState('');
  const spoer = useSpoerEieren();
  const lukkSp = useLukkSporsmaal();
  const feltRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => feltRef.current?.focus(), []);

  if (!kilde) {
    return (
      <div className="h-panel-spor">
        <p className="hb-dempet">Spørsmålet går til den som eier kilden. Velg kilden først.</p>
        <div>
          <Tekstknapp onClick={onKilder}>Kilder hos {tenant}</Tekstknapp>
        </div>
      </div>
    );
  }
  if (kilde.owner.id === megId) return <p className="hb-dempet">Du eier denne kilden.</p>;
  const eier = navnAv(kilde.owner);
  if (sporsmaal) {
    const forSent = sporsmaal.status === 'aapen' && sporsmaal.answerBy < new Date().toISOString().slice(0, 10);
    return (
      <div className="h-panel-spor">
        <div className="hb-kort h-panel-spurt">
          <div className="hb-kort-hode">
            <b>
              <Ikon navn="help-circle-outline" /> Du spurte {eier}
            </b>
            <span className="hb-liten">{kortDatoAv(sporsmaal.askedAt)}</span>
          </div>
          <p className="hb-tekst">{sporsmaal.question}</p>
          {sporsmaal.status === 'besvart' ? (
            <p className="hb-dempet">{eier} har svart — svaret står ved måltallet.</p>
          ) : forSent ? (
            <p className="hb-dempet">{eier} har ikke svart. Du kan sende inn — vurdereren tar det derfra.</p>
          ) : (
            <p className="hb-dempet">
              Svar innen {kortDato(sporsmaal.answerBy)}. {eier} kan se utkastet til du sender det inn eller lukker spørsmålet.
            </p>
          )}
          {sporsmaal.status === 'aapen' ? (
            <div>
              <Tekstknapp onClick={() => lukkSp.mutate(sporsmaal.id)}>Lukk spørsmålet</Tekstknapp>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const kort = tekst.trim().length < MIN_SPORSMAAL;
  const send = () => {
    if (kort || spoer.isPending || !id) return;
    spoer.mutate({ hypoteseId: id, kildeId: kilde.id, sporsmaal: tekst.trim() });
  };
  const feil = spoer.error ? (erApiFeil(spoer.error) && !erNettfeil(spoer.error) && spoer.error.code !== 'unknown' ? spoer.error.brukertekst : 'Fikk ikke sendt spørsmålet. Prøv igjen.') : null;
  return (
    <div className="h-panel-spor">
      <div className="h-felt">
        <label htmlFor="h-spor-felt" className="h-etikett">
          Hva lurer du på?
        </label>
        <textarea
          ref={feltRef}
          id="h-spor-felt"
          className="h-input h-tekstfelt"
          rows={4}
          maxLength={280}
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          onKeyDown={(e) => {
            // Ctrl+Enter hører til feltet som har fokus (DD-11 presisert): her er det spørsmålet, ikke «Neste».
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              send();
            }
          }}
          aria-describedby="h-spor-info"
        />
      </div>
      <p id="h-spor-info" className="h-panel-info">
        <Ikon navn="information-circle-outline" />
        <span>
          {eier} kan se dette utkastet til du sender det inn eller lukker spørsmålet. {fornavn(kilde.owner)} ser hvem som spør.
        </span>
      </p>
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="h-panel-handling">
        <button type="button" className="panel-knapp" aria-disabled={kort || spoer.isPending || undefined} aria-describedby={kort ? 'h-spor-grunn' : undefined} aria-keyshortcuts="Control+Enter" onClick={send}>
          <Ikon navn={kort ? 'lock-closed-outline' : 'send-outline'} />
          Send spørsmålet <kbd>Ctrl ↵</kbd>
        </button>
        {kort ? (
          <span id="h-spor-grunn" className="h-grunn">
            Skriv spørsmålet — minst {MIN_SPORSMAAL} tegn.
          </span>
        ) : null}
      </div>
    </div>
  );
}
